import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'

export default function SignUp() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!fullName.trim() || !email.trim() || !password) return
    if (password.length < 8) {
      setError(t('auth.error_password_too_short'))
      return
    }

    setLoading(true)
    setError('')

    // 1. Create auth user
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        data: { full_name: fullName.trim() },
      },
    })

    if (signUpError) {
      const msg = signUpError.message || ''
      if (/already registered/i.test(msg) || /already been used/i.test(msg)) {
        setError(t('auth.error_email_already_used'))
      } else {
        setError(msg || t('auth.error_generic'))
      }
      setLoading(false)
      return
    }

    const user = signUpData.user
    if (!user) {
      setError(t('auth.error_generic'))
      setLoading(false)
      return
    }

    // 2. If email confirmation is required, show confirmation screen
    //    (Supabase returns user object but no session in that case)
    if (!signUpData.session) {
      setAwaitingConfirmation(true)
      setLoading(false)
      return
    }

    // 3. We have a session — finish profile setup
    //    Profile row is auto-created by Supabase trigger; update full_name if needed.
    await supabase.from('profiles').upsert({
      id: user.id,
      full_name: fullName.trim(),
    }, { onConflict: 'id' })

    // 4. Do NOT seed tags at signup — the onboarding Step 2 asks the user
    //    which tag types they use, and seeds only those via seed_tags_for_user.
    //    Seeding here would clutter their Settings with tag types they didn't pick.

    setLoading(false)
    navigate('/')
  }

  if (awaitingConfirmation) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.brand}>
            <span style={styles.brandMerq}>Merq</span>
            <span style={styles.brandLog}>Log</span>
          </div>
          <div style={styles.confirmIcon}>📧</div>
          <h2 style={styles.confirmTitle}>{t('auth.check_email_title')}</h2>
          <p style={styles.confirmBody}>
            {t('auth.check_email_body', { email })}
          </p>
          <Link to="/login" style={{ ...styles.switchLink, display: 'block', textAlign: 'center', marginTop: '20px' }}>
            {t('auth.back_to_login')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.brand}>
          <span style={styles.brandMerq}>Merq</span>
          <span style={styles.brandLog}>Log</span>
        </div>
        <p style={styles.tagline}>{t('app.tagline')}</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>
            <span style={styles.labelText}>{t('auth.full_name')}</span>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoComplete="name"
              autoFocus
              required
              style={styles.input}
            />
          </label>

          <label style={styles.label}>
            <span style={styles.labelText}>{t('auth.email')}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              style={styles.input}
            />
          </label>

          <label style={styles.label}>
            <span style={styles.labelText}>{t('auth.password')}</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
              style={styles.input}
            />
            <span style={styles.hint}>{t('auth.password_hint')}</span>
          </label>

          {error && <div style={styles.error}>{error}</div>}

          <button type="submit" disabled={loading} style={{
            ...styles.submit,
            opacity: loading ? 0.7 : 1,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}>
            {loading ? t('auth.creating_account') : t('auth.signup')}
          </button>
        </form>

        <div style={styles.divider} />

        <div style={styles.switchRow}>
          <span style={styles.switchText}>{t('auth.have_account')}</span>
          <Link to="/login" style={styles.switchLink}>
            {t('auth.login')}
          </Link>
        </div>
      </div>
    </div>
  )
}

const plum = '#1E1040'
const teal = '#0D9488'

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    background: 'linear-gradient(135deg, #F7F9FC 0%, #E6FFFA 100%)',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    background: '#fff',
    borderRadius: '20px',
    padding: '40px 32px',
    boxShadow: '0 10px 40px rgba(30, 16, 64, 0.08)',
    border: '1px solid rgba(30, 16, 64, 0.06)',
  },
  brand: {
    fontFamily: "'Fraunces', serif",
    fontSize: '36px',
    fontWeight: 900,
    letterSpacing: '-1px',
    textAlign: 'center',
    marginBottom: '6px',
  },
  brandMerq: { color: plum },
  brandLog: { color: teal, fontStyle: 'italic' },
  tagline: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '14px',
    fontWeight: 500,
    margin: '0 0 32px 0',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  labelText: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  input: {
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1.5px solid #E5E7EB',
    fontSize: '15px',
    color: plum,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  hint: {
    fontSize: '11px',
    color: '#9CA3AF',
    fontWeight: 500,
    marginTop: '2px',
  },
  error: {
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    color: '#B91C1C',
    fontSize: '13px',
    padding: '10px 12px',
    borderRadius: '8px',
    fontWeight: 500,
  },
  submit: {
    marginTop: '6px',
    padding: '14px',
    background: teal,
    color: '#042f2e',
    border: 'none',
    borderRadius: '100px',
    fontSize: '14px',
    fontWeight: 700,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 4px 14px ${teal}40`,
  },
  divider: {
    height: '1px',
    background: '#E5E7EB',
    margin: '24px 0 20px 0',
  },
  switchRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '6px',
    fontSize: '14px',
  },
  switchText: { color: '#6B7280' },
  switchLink: {
    color: teal,
    fontWeight: 700,
    textDecoration: 'none',
  },
  // Email-confirmation success screen
  confirmIcon: {
    fontSize: '48px',
    textAlign: 'center',
    margin: '20px 0 12px 0',
  },
  confirmTitle: {
    fontFamily: "'Fraunces', serif",
    fontSize: '24px',
    fontWeight: 800,
    color: plum,
    textAlign: 'center',
    margin: '0 0 12px 0',
    letterSpacing: '-0.5px',
  },
  confirmBody: {
    fontSize: '14px',
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 1.6,
    margin: 0,
  },
}
